import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { answerText } from '@/components/worksheets/answerText';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const QUIET = ['heading', 'instruction'];

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined,
    { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** A single form, laid out for paper. No app chrome — this page is only ever printed. */
export default function PrintIntakePage() {
  const { responseId } = useParams();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState('');

  useEffect(() => {
    axios.get(`${API}/intake-forms/response/${responseId}`, auth())
      .then((r) => setData(r.data))
      .catch((e) => setFailed(e?.response?.data?.detail || 'Could not load that form'));
  }, [responseId]);

  useEffect(() => {
    if (data) {
      // Let the fonts settle before the dialog opens, or the first page measures wrong.
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (failed) return <div style={{ padding: 40, fontFamily: 'Georgia, serif' }}>{failed}</div>;
  if (!data) return <div style={{ padding: 40, fontFamily: 'Georgia, serif' }}>Preparing the sheet…</div>;

  const r = data.response;
  const fields = (r.fields_snapshot || []).filter((f) => !QUIET.includes(f.type));

  return (
    <div className="print-sheet" data-testid="print-intake-sheet">
      <style>{`
        @page { margin: 18mm 16mm; }
        body { background: #fff; }
        .print-sheet { font-family: Georgia, 'Times New Roman', serif; color: #1a1a16;
          max-width: 190mm; margin: 0 auto; padding: 24px; }
        .ps-head { border-bottom: 2px solid #1a1a16; padding-bottom: 10px; margin-bottom: 18px; }
        .ps-title { font-size: 20pt; margin: 0 0 4px; }
        .ps-meta { font-size: 10pt; color: #4a4a44; margin: 2px 0; }
        .ps-q { margin-bottom: 14px; page-break-inside: avoid; }
        .ps-label { font-size: 10pt; font-weight: 700; margin: 0 0 3px; }
        .ps-answer { font-size: 11pt; margin: 0; white-space: pre-line; line-height: 1.45; }
        .ps-blank { color: #8a8a84; font-style: italic; }
        .ps-table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 9.5pt; }
        .ps-table th, .ps-table td { border: 1px solid #9a9a94; padding: 4px 6px;
          text-align: left; vertical-align: top; }
        .ps-table th { background: #f1efe9; font-size: 9pt; }
        .ps-foot { margin-top: 26px; border-top: 1px solid #c9c9c2; padding-top: 8px;
          font-size: 8.5pt; color: #6a6a64; line-height: 1.5; }
        .ps-print-btn { margin-bottom: 16px; font-family: system-ui, sans-serif; font-size: 13px;
          padding: 7px 16px; border: 1px solid #1a1a16; border-radius: 999px; background: #fff;
          cursor: pointer; }
        @media print { .ps-print-btn { display: none; } }
      `}</style>

      <button className="ps-print-btn" onClick={() => window.print()} data-testid="print-again-btn">
        Print this form
      </button>

      <div className="ps-head">
        <h1 className="ps-title">{r.form_title}</h1>
        <p className="ps-meta"><strong>Client:</strong> {r.client_name || r.client_email}
          {r.client_email && r.client_name ? ` · ${r.client_email}` : ''}</p>
        {r.session_title && (
          <p className="ps-meta"><strong>Session:</strong> {r.session_title}
            {r.scheduled_at ? ` · ${when(r.scheduled_at)}` : ''}</p>
        )}
        <p className="ps-meta"><strong>Completed:</strong> {when(r.submitted_at)}
          {' · '}{r.timing === 'after' ? 'after the session' : 'before the session'}</p>
        {data.practitioner_name && (
          <p className="ps-meta"><strong>Practitioner:</strong> {data.practitioner_name}</p>
        )}
      </div>

      {fields.length === 0 && <p className="ps-answer ps-blank">This form had no questions on it.</p>}

      {fields.map((f) => {
        const shown = answerText(f, r.answers?.[f.id]);
        if (f.type === 'table' || f.type === 'table_fill') {
          const columns = f.table_columns || [];
          const rows = f.type === 'table'
            ? (f.table_rows || [])
            : (Array.isArray(r.answers?.[f.id]) ? r.answers[f.id] : []);
          return (
            <div className="ps-q" key={f.id}>
              <p className="ps-label">{f.label}</p>
              {columns.length === 0 || rows.length === 0
                ? <p className="ps-answer ps-blank">left blank</p>
                : (
                  <table className="ps-table">
                    <thead>
                      <tr>{columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {rows.map((row, ri) => (
                        <tr key={ri}>
                          {columns.map((_, ci) => (
                            <td key={ci}>{Array.isArray(row) ? (row[ci] ?? '') : ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          );
        }
        return (
          <div className="ps-q" key={f.id}>
            <p className="ps-label">{f.label}</p>
            {shown
              ? <p className="ps-answer">{shown}</p>
              : <p className="ps-answer ps-blank">left blank</p>}
          </div>
        );
      })}

      <p className="ps-foot">{data.notice}</p>
    </div>
  );
}
